goog.provide('el8.uiwidget.Form');

goog.require('el8.service.infoField');
goog.require('el8.service.tz');
goog.require('el8.template.uiwidget.Form');
goog.require('el8.uiwidget.addDatepicker');
goog.require('el8.uiwidget.ClickItem');
goog.require('el8.uiwidget.FormButtonSet');
goog.require('el8.uiwidget.Icd10ListEditor');
goog.require('el8.uiwidget.listeditor');
goog.require('el8.uiwidget.LegacyMindreader');
goog.require('el8.uiwidget.multifieldListeditor');
goog.require('el8.uiwidget.nestedListeditor');
goog.require('el8.uiwidget.NewMindreader');
goog.require('el8.uiwidget.TagInput');
goog.require('el8.util.base');


/**
 * This provides an abstraction on top of a <form>.
 * We use this extensively throughout the application.
 * Some of the advantages over raw <form> / jQuery:
 *
 * 1. Makes it easier to setup listeditor, autocompletes ("mindreaders"),
 *    and other widgets for inputs inside your form
 *    (via listeditorOpts, mindreaderOpts, etc. in the field spec)
 * 2. Provides a simpler/more consistent API for getting/setting
 *    values in the form, including handling checkboxes, radios, etc.
 *    (see form.getVal / form.setVal)
 * 3. Sets up highlighting/unhighlighting of fields as you tab through them
 * 4. Manages buttons linked to your form
 * 5. Manages tab order so that when you tab past your last button, you'll
 *    loop back to the beginning of the form
 *
 * You will still need to create the HTML for the form/fields in a template.
 *
 * For an example dialog usage, see editReportDetails.js
 * (and appchart.dialog.editReportDetails.html).
 * For an example usage outside a dialog, see commentReplyForm.js.
 */
el8.uiwidget.Form = function(spec) {

    spec = spec || {};
    this._isInit = false;

    this._container = spec.container || null;
    this._buttonElt = spec.buttonElt || null;

    this._formElt = null;  /* auto-created within container as needed */

    this._validationOpts = spec.validationOpts || null;

    /**
     * fields is an array of dicts that look like:
     * { name: @param {String} (required), (string to set 'name' attribute)
     *   type: @param {String} (required),
     *      - 'input', 'textarea', 'tmpl', 'select' (template)
     *   selector: @param {String} (optional), (string jquery selector)
     *   tmplFn: @param {Function} (optional), (closure template)
     *   validate: @param {Object} (optional),
     *   maxLength: @param {Number} (optional),
     *   rows: @param {Number} (optional),
     *   height: @param {Number} (optional),
     *   infoText: @param {String} (optional),
     *   noGrow: @param {Boolean} (optional),
     *   defaultVal: @param {String} (optional),
     *   mindreaderOpts: @param {Object} (optional)
     *   listeditorOpts: @param {Object} (optional)
     *   isDate: @param {Boolean} (optional) }
     */
    this._fields = spec.fields || [];
    this._fieldArray = [];
    this._buttons = spec.buttons || [];
    this._buttonSet = spec.buttonSetConstructor ?
        (new spec.buttonSetConstructor(this)) : (new el8.uiwidget.FormButtonSet(this));
    this._buttonSet.setContainer(this._container);

    this._firstFieldSelector = spec.firstFieldSelector ||
        (this._fields.length ?
         (this._fields[0].selector || ':input[name="'+this._fields[0].name+'"]') : null);
    this._firstFocusSelector = spec.firstFocusSelector ||  this._firstFieldSelector;
    this._noFirstFocus = spec.noFirstFocus || null;

    this._validator = null;

    this._multifieldListeditorSpecs = spec.multifieldListeditorSpecs || null;

    this._defaultVals = {};
    this._infoFields = {};
    this._mindreaders = {};
    this._listeditors = {};
    this._icd10Listeditors = {};
    this._multifieldListeditors = {};
    this._clickItems = {};
    this._tagInputs = {};

    this._disabled = false;

};

el8.uiwidget.Form.prototype.getContainer = function() {
    return this._container;
};
el8.uiwidget.Form.prototype.setContainer = function(container) {
    this._container = container;
    return this;
};
el8.uiwidget.Form.prototype.setButtonElt = function(buttonElt) {
    this._buttonElt = buttonElt;
    return this;
};
el8.uiwidget.Form.prototype.getButtonSet = function() {
    if (!this._buttonSet) {
        this._buttonSet = new el8.uiwidget.FormButtonSet(this);
    }
    return this._buttonSet;
};
el8.uiwidget.Form.prototype.addButton = function(var_args) {
    this.getButtonSet().addButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.addButtonSane = function(var_args) {
    this.getButtonSet().addButtonSane.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.removeButton = function(var_args) {
    this.getButtonSet().removeButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.updateButtonCaption = function(var_args) {
    this.getButtonSet().updateButtonCaption.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.enableButton = function(var_args) {
    this.getButtonSet().enableButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.disableButton = function(var_args) {
    this.getButtonSet().disableButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.hideButton = function(var_args) {
    this.getButtonSet().hideButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.showButton = function(var_args) {
    this.getButtonSet().showButton.apply(this.getButtonSet(), arguments);
};
el8.uiwidget.Form.prototype.bindButtonEvents = function() {
    this._buttonSet.bindEvents();
};
el8.uiwidget.Form.prototype.unbindButtonEvents = function() {
    this._buttonSet.unbindEvents();
};
el8.uiwidget.Form.prototype.renderButtonSet = function() {
    this._buttonSet.render();
};

/* These functions make it easier to change the text of standard Form buttons
 * (and reduce verbosity) */
el8.uiwidget.Form.prototype.addCancelButton = function(triggerFn, isPrimary, testId) {
    this.addButtonSane('cancel', 'Cancel', triggerFn, {isPrimary: isPrimary, testId: testId});
};
el8.uiwidget.Form.prototype.addSaveButton = function(triggerFn, isPrimary) {
    this.addButton('save', 'Save', triggerFn, isPrimary);
};
el8.uiwidget.Form.prototype.addSaveAndAnotherButton = function(triggerFn, isPrimary) {
    this.addButton('save-and-another', 'Save & Add Another', triggerFn, isPrimary);
};
el8.uiwidget.Form.prototype.addDiscardButton = function(triggerFn, isPrimary) {
    this.addButton('cancel', 'Discard', triggerFn, isPrimary);
};
el8.uiwidget.Form.prototype.addDiscardChangesButton = function(triggerFn, isPrimary) {
    this.addButton('cancel', 'Discard Changes', triggerFn, isPrimary);
};
el8.uiwidget.Form.prototype.showDiscardChangesCaption = function() {
    this.updateButtonCaption('cancel', 'Discard Changes');
};
el8.uiwidget.Form.prototype.showDiscardCaption = function() {
    this.updateButtonCaption('cancel', 'Discard');
};

el8.uiwidget.Form.prototype.addFields = function(newFields) {
    var that = this;
    newFields.forEach(function(f) { that.addField(f); });
};
el8.uiwidget.Form.prototype.addField = function(field) {
    this._fields.push(field);
    if (this._isInit) {
        this._initField(field);
    }
};
el8.uiwidget.Form.prototype.removeFields = function(deadFieldNames) {
    var that = this;
    deadFieldNames.forEach(function(name) { that.removeField(name); });
};
el8.uiwidget.Form.prototype.removeField = function(fieldName) {
    for (var i=0; i < this._fields.length; i++) {
        if (this._fields[i].name === fieldName) {
            this._fields.splice(i, 1);
        }
    }

    if (fieldName in this._mindreaders) {
        delete this._mindreaders[fieldName];
    }
    if (fieldName in this._listeditors) {
        delete this._listeditors[fieldName];
    }
    if (fieldName in this._icd10Listeditors) {
        delete this._icd10Listeditors[fieldName];
    }
    if (fieldName in this._clickItems) {
        delete this._clickItems[fieldName];
    }
    if (fieldName in this._tagInputs) {
        delete this._tagInputs[fieldName];
    }
};

el8.uiwidget.Form.prototype.showField = function(name) {
    this.getInputField(name).closest('.el8FieldSection').show(); // TODO dependency on CSS class
};

el8.uiwidget.Form.prototype.hideField = function(name) {
    this.getInputField(name).closest('.el8FieldSection').hide(); // TODO dependency on CSS class
};

el8.uiwidget.Form.prototype.disableField = function(name) {
    var $input = this.getInputField(name);
    $input.blur();
    $input.prop('disabled', true);
    if ($input.hasClass('hasDatepicker')) {
        $input.datepicker('disable');
    }
};

el8.uiwidget.Form.prototype.enableField = function(name) {
    var $input = this.getInputField(name);
    $input.prop('disabled', false);
    if ($input.hasClass('hasDatepicker')) {
        $input.datepicker('enable');
    }
};

el8.uiwidget.Form.prototype.isFieldEnabled = function(name) {
    var $input = this.getInputField(name);
    return Boolean($input.prop('disabled'));
};

el8.uiwidget.Form.prototype.clearField = function(name) {
    var infoField = this._infoFields[name];
    if (infoField && typeof infoField.setVal === 'function') {
        infoField.setVal('');
    } else {
        console.error('[Form.clearField] No clearable field for "' + name + '" (missing infoField or setVal)');
    }
};

el8.uiwidget.Form.prototype.getInfoField = function(name) {
    return this._infoFields[name];
};

el8.uiwidget.Form.prototype.getFieldNames = function() {
    var resultSet = [];
    this._fields.map(function(field) {
        resultSet.push(field.name);
    });
    return resultSet;
};

el8.uiwidget.Form.prototype._initField = function(field) {

    var elt = null;
    var that = this;

    if (field.type === 'react') {
        if (!field.hasReactMounted) {
            field.reactRenderCaller(
                function() {
                    // After React component mounts, reinitialize the field once
                    if (field) {
                        field.hasReactMounted = true;
                        // Remove form's old InfoField for this field if it exists
                        if (that._infoFields[field.name]) {
                            delete that._infoFields[field.name];
                        }
                        // Reinitialize the field now that react has generated the textarea
                        that._initField(field);
                    }
                }
            );
        } else {
            // If react has mounted we should be able to find the input element
            elt = that._container.find(
                '#' + field.containerId + ' input:first, #' + field.containerId + ' textarea:first');
        }
    } else if (field.selector) {
        elt = $(field.selector, this._formElt);
        // Make sure name is specified correctly
        if (field.name) { elt.attr('name', field.name); }

    } else if (field.elt) {
        elt = field.elt;
        if (field.name) { elt.attr('name', field.name); }

    } else if (field.tmplFn) {
        var toAdd = $(field.tmplFn(field));
        if (field.tmplStubSelector) {
            $(field.tmplStubSelector, this._formElt).replaceWith(toAdd);
        } else {
            this._formElt.append(toAdd);
        }
        elt = $('input:first, textarea:first', toAdd);

    } else if (field.type === 'input') {
        var div = $(el8.template.uiwidget.Form.input(field));
        this._formElt.append(div);
        elt = div.find('input');

    } else if (field.type === 'textarea') {
        var div = $(el8.template.uiwidget.Form.textarea(field));
        this._formElt.append(div);
        elt = div.find('textarea');
    }

    if (!elt && field.name) {
        elt = $(':input[name="'+field.name+'"]', this._formElt);
    }

    if ((elt.is('input') &&
                (!elt.is('input[type="radio"]') && !elt.is('input[type="checkbox"]'))) ||
            elt.is('textarea')) {
        this._infoFields[field.name] = this._createInfoField({
            field: elt,
            maxLength: field.maxLength || (elt.is('input') ? 100 : null),
            rows: field.rows || null,
            infoText: field.infoText || '',
            noGrow: field.noGrow,
            height: field.height || null,
            doShrink: field.doShrink,
            size: field.size,
            handleChangeOnSetVal: field.handleChangeOnSetVal,
            changeFn: field.handleChangeOnSetVal ? field.changeFn : null
        });
        if (field.mindreaderOpts) {
            var mrOpts = jQuery.isFunction(field.mindreaderOpts) ?
                field.mindreaderOpts() : field.mindreaderOpts;
            if (field.validate) {
                mrOpts['hasValidation'] = true;
            }
            if (field.newMindreader) {
                this._mindreaders[field.name] = (new el8.uiwidget.NewMindreader(mrOpts)).init(elt);
            } else {
                this._mindreaders[field.name] = (new el8.uiwidget.LegacyMindreader(mrOpts)).init(elt);
            }
        } else if (field.listeditorOpts) {
            var leOpts = jQuery.isFunction(field.listeditorOpts) ?
                field.listeditorOpts() : field.listeditorOpts;

            if (field.listeditorOpts.nestingSpec) {
                // If there's a nestingSpec, create a nestedListeditor subclass
                this._listeditors[field.name] =
                    (new el8.uiwidget.nestedListeditor(leOpts)).init(this._infoFields[field.name]);
            } else {
                this._listeditors[field.name] =
                    (new el8.uiwidget.listeditor(leOpts)).init(this._infoFields[field.name]);
            }
        }

    /* These operate on an elt inside the form that isn't an input */
    } else if (field.clickItemOpts) {
        var clickItemOpts = jQuery.isFunction(field.clickItemOpts) ?
            field.clickItemOpts() : field.clickItemOpts;
        if (field.infoText) {
            clickItemOpts['infoText'] = field.infoText;
        }
        clickItemOpts['name'] = field.name;
        if (field.validate) {
            clickItemOpts.mindreaderOpts = clickItemOpts.mindreaderOpts || {};
            clickItemOpts.mindreaderOpts['hasValidation'] = true;
        }
        this._clickItems[field.name] =
            (new el8.uiwidget.ClickItem(clickItemOpts)).init({elt: elt});

    } else if (field.tagInputOpts) {
        var tagInputOpts = jQuery.isFunction(field.tagInputOpts) ?
            field.tagInputOpts() : field.tagInputOpts;
        if (field.infoText) {
            tagInputOpts['infoText'] = field.infoText;
        }
        if (field.maxLength) {
            tagInputOpts['maxLength'] = field.maxLength;
        }
        tagInputOpts['tagContainer'] = elt;
        this._tagInputs[field.name] = (new el8.uiwidget.TagInput(tagInputOpts)).init();

    } else if (field.icd10ListeditorOpts) {
        var icd10LeOpts = jQuery.isFunction(field.icd10ListeditorOpts) ?
            field.icd10ListeditorOpts() : field.icd10ListeditorOpts;
        icd10LeOpts['listContainer'] = elt;
        icd10LeOpts['form'] = this;
        this._icd10Listeditors[field.name] =
            (new el8.uiwidget.Icd10ListEditor(icd10LeOpts)).init();
    }

    if ((elt.is('input:checkbox') || elt.is('input:radio'))) {
        elt.on('focus', function(e) {
            $(e.target).parent('label').addClass('focus');
        }).on('blur', function(e) {
            $(e.target).parent('label').removeClass('focus');
        });
    }

    if (field.datePickerOpts) {
        el8.uiwidget.addDatepicker(this._infoFields[field.name], field.datePickerOpts);
    }

    if (field.timePickerOpts) {
        var timePickerOpts = jQuery.isFunction(field.timePickerOpts) ?
            field.timePickerOpts() : field.timePickerOpts;
        var increment = timePickerOpts.increment || 15;
        this._infoFields[field.name].getInputField().timeEntry({
            timeSteps: [1, increment],
            ampmPrefix: ' ',
            ampmNames: ['am', 'pm'],
            spinnerImage: "/static/images/spinnerUpDown.png",
            spinnerSize: [15, 16, 8],
        });
        if (timePickerOpts.timeSelectedFn) {
            that._container.on('blur', ':input[name="'+field.name+'"]', function(e) {
                timePickerOpts.timeSelectedFn();
            });
        }
    }

    if (elt.is('select') && field.chosenOpts) {
        elt.chosen(field.chosenOpts);
    }

    if (field.defaultVal) {
        this._defaultVals[field.name] = jQuery.isFunction(field.defaultVal) ? field.defaultVal() : field.defaultVal;
    }

    if (!elt.is('input:radio')) {
        this._fieldArray.push(elt);
    } else {
        elt.each(function(i, _elt) {
            that._fieldArray.push($(_elt));
        });
    }

    if (field.blurFn) {
        var that = this;
        this._formElt.on('blur', ':input[name="'+elt.attr('name')+'"]', function(e) {
            field.blurFn(e, that);
        });
    }

    if (field.focusFn) {
        var that = this;
        this._formElt.on('focus', ':input[name="'+elt.attr('name')+'"]', function(e) {
            field.focusFn(e, that);
        });
    }

    if (field.changeFn) {
        var that = this;
        this._formElt.on('change', ':input[name="'+elt.attr('name')+'"]', function(e) {
            field.changeFn(e, that);
        });
    }
};

el8.uiwidget.Form.prototype._initFields = function() {
    for (var i=0; i<this._fields.length; i++) {
        this._initField(this._fields[i]);
    }
};

el8.uiwidget.Form.prototype._initButtons = function() {
    for (var i=0; i<this._buttons.length; i++) {
        this.addButton.apply(this, this._buttons[i]);
    }
};

el8.uiwidget.Form.prototype._init = function() {
    if (this._isInit) { return; }

    // If container is not a form, then add a form
    if (!this._container.is('form')) {
        this._formElt = $('<form>'); // XSS OK -KF
        this._container.append(this._formElt);
    } else {
        this._formElt = this._container;
    }

    // Disable the submit()
    this._formElt.submit(function(e) { return false; });

    // This may add fields to the form, so do it before calling initFields
    if (this._multifieldListeditorSpecs && this._multifieldListeditorSpecs.length) {
        var that = this;
        this._multifieldListeditorSpecs.forEach(function(mleOpts) {
            mleOpts = jQuery.isFunction(mleOpts) ? mleOpts() : mleOpts;
            mleOpts.form = that;
            that._multifieldListeditors[mleOpts.groupName] =
                (new el8.uiwidget.multifieldListeditor(mleOpts)).init();
        });
    }

    this._initFields();
    this._initButtons();
    if (this._buttonElt) {
        this._buttonSet
            .setContainer(this._buttonElt)
            .render();
    }
    var that = this;
    if (this._firstFieldSelector) {
        this._buttonSet.getTabCatcher().on('focus', function(e) {
            $(that._firstFieldSelector, that._container)
                .scrollintoview()
                .focusSelect();
        });
    }
    if (this._validationOpts) {
        var opts = $.extend({}, this._validationOpts);
        var rules = {};
        var messages = {};
        for (var i=0; i<this._fields.length; i++) {
            if (this._fields[i].validate) {
                rules[this._fields[i].name] = this._fields[i].validate;
                if (this._fields[i].validateMsgs) {
                    messages[this._fields[i].name] = this._fields[i].validateMsgs;
                }
            }
        }
        opts['rules'] = rules;
        if (!el8.util.isEmptyObj(messages)) {
            opts['messages'] = messages;
        }
        // Always add this, so infoText doesn't get counted as input
        opts['parseVal'] = function(elt) {
            var val = elt.value;
            if ($(elt).attr('_infotext')) {
                return (val === $(elt).attr('_infotext')) ? '' : val;
            } else {
                return val;
            }
        };
        this._validator = this._formElt.validate(opts);
    }

    this._isInit = true;
};

el8.uiwidget.Form.prototype.isValid = function() {
    return this._formElt.valid();
};

el8.uiwidget.Form.prototype.reset = function() {

    if (this._validator) {
        this._validator.resetForm();
    } else {
        if (this._formElt) { this._formElt[0].reset(); }
    }

    for (var mr in this._mindreaders) {
        if (this._mindreaders.hasOwnProperty(mr)) {
            this._mindreaders[mr].hide();
        }
    }

    for (var le in this._listeditors) {
        if (this._listeditors.hasOwnProperty(le)) {
            this._listeditors[le].reset();
        }
    }

    for (var le in this._icd10Listeditors) {
        if (this._icd10Listeditors.hasOwnProperty(le)) {
            this._icd10Listeditors[le].reset();
        }
    }

    for (var ci in this._clickItems) {
        if (this._clickItems.hasOwnProperty(ci)) {
            this._clickItems[ci].reset();
        }
    }

    for (var ti in this._tagInputs) {
        if (this._tagInputs.hasOwnProperty(ti)) {
            this._tagInputs[ti].reset();
        }
    }

    for (var mle in this._multifieldListeditors) {
        if (this._multifieldListeditors.hasOwnProperty(mle)) {
            this._multifieldListeditors[mle].reset();
        }
    }

    var that = this;
    $(':input', this._formElt).each(function() {
        var name = $(this).attr('name');
        if (!el8.util.isNone(that._defaultVals[name])) {
            that.setVal(name, that._defaultVals[name]);
        } else {
            that.resetInfoField(name);
        }
    });
};

el8.uiwidget.Form.prototype.resetInfoField = function(name) {
    if (this._infoFields[name]) {
        this._infoFields[name].init();
        var $inputField = this._infoFields[name].getInputField();
        if ($inputField.is('textarea')) {
            //reset height (for autogrowing textareas) on form reset
            el8.util.dom.growShrinkTextarea($inputField, null, $inputField.attr('height'));
        }
    }
};

el8.uiwidget.Form.prototype.disable = function() {
    $(':input', this._formElt).prop('disabled', true);
    $('.ui-datepicker-trigger', this._formElt).hide();
    if (this.getButtonSet()) {
        this.getButtonSet().disable();
    }
    this._disabled = true;
};
el8.uiwidget.Form.prototype.enable = function() {
    $(':input', this._formElt).prop('disabled', false);
    $('.ui-datepicker-trigger', this._formElt).show();
    if (this.getButtonSet()) {
        this.getButtonSet().enable();
    }
    this._disabled = false;
};
el8.uiwidget.Form.prototype.isEnabled = function() {
    return !this._disabled;
};

el8.uiwidget.Form.prototype.getMindreader = function(name) {
    if (this._mindreaders[name]) { return this._mindreaders[name]; }
    if (this._listeditors[name] && this._listeditors[name].getMindreader()) {
        return this._listeditors[name].getMindreader();
    }
    return undefined;
};

el8.uiwidget.Form.prototype.getMindreaderItem = function(name) {
    return this.getMindreader(name) ?
        this.getMindreader(name).getSelectedItem() : null;
};

el8.uiwidget.Form.prototype.setMindreaderItem = function(name, item, noTrigger) {
    if (this.getMindreader(name)) {
        this.getMindreader(name).setSelectedItem(null, item, noTrigger);
    }
};

el8.uiwidget.Form.prototype.setMindreaderSearchData = function(name, data) {
    if (this.getMindreader(name)) {
        this.getMindreader(name).updateSearchData(data);
    }
};

el8.uiwidget.Form.prototype.getClickItem = function(name) {
    return this._clickItems[name] || null;
};

el8.uiwidget.Form.prototype.getClickItemSelected = function(name) {
    return this._clickItems[name] ? this._clickItems[name].getItem() : null;
};

el8.uiwidget.Form.prototype.getClickItemId = function(name) {
    return this._clickItems[name] ? this._clickItems[name].getItemId() : null;
};

el8.uiwidget.Form.prototype.setClickItemSelected = function(name, item, noTrigger) {
    if (this._clickItems[name]) {
        this._clickItems[name].setItem(item, noTrigger);
    }
};

el8.uiwidget.Form.prototype.getTagInput = function(name) {
    return this._tagInputs[name] || null;
};

el8.uiwidget.Form.prototype.getListeditor = function(name) {
    return this._listeditors[name] || null;
};

el8.uiwidget.Form.prototype.getIcd10Listeditor = function(name) {
    return this._icd10Listeditors[name] || null;
};

el8.uiwidget.Form.prototype.getMultifieldListeditor = function(name) {
    return this._multifieldListeditors[name] || null;
};

el8.uiwidget.Form.prototype.getVal = function(name) {
    if (this._infoFields[name]) {
        return this._infoFields[name].getVal();

    } else if (this._clickItems[name]) {
        /* Returns either an ID if available, else the text entered */
        return this._clickItems[name].getItemVal();

    } else if (this._tagInputs[name]) {
        /* Returns a list of IDs */
        return this._tagInputs[name].getSelectedIds();

    } else {
        var elt = $('[name="'+name+'"]', this._formElt);
        if (!elt.length) { return ''; }
        if (elt.is('input:radio')) {
            var val = elt.filter(':checked').val();
            // if we turn on the `castToBool` flag, this radio represents a toggle
            // and we just want to know whether it's true or false. Without this flag,
            // we have to do gymnastics because 'false' and '0' are both truthy.
            var fieldSpec = this._getFieldSpec(name);
            if (fieldSpec.castToBool) {
                if (val === 'true' || val === '1') { return true; }
                if (val === 'false' || val === '0') { return false; }
                return Boolean(val);
            }
            return val;
        } else if (elt.is('input:checkbox')) {
            return elt.is(':checked');
        } else if (elt.is('input:file') && elt[0].files && elt[0].files.length) {
            // If you want a multi-file input, the caller will have to custom manage that
            return elt[0].files[0];
        } else {
            return elt.val();
        }
    }
};

el8.uiwidget.Form.prototype.setVal = function(name, val, noTrigger) {
    if (this._infoFields[name]) {
        this._infoFields[name].setVal(val);

    } else if (this._clickItems[name]) {
        /* "val" expected to be a string here */
        this._clickItems[name].setFreeText(val);

    } else {
        var elt = $('[name="'+name+'"]', this._formElt);
        if (!elt.length) { return; }
        if (elt.is('input:radio')) {
            // the other side of the `castToBool` logic above: we'll try to make these
            // boolean radios just work if you pass in a range of reasonable values
            var fieldSpec = this._getFieldSpec(name);
            if (fieldSpec.castToBool && (val === true || val === 'true' ||
                                         val === '1' || val === 1) ) {
                elt.filter('[value="1"], [value="true"]').prop('checked', true);
            } else if (fieldSpec.castToBool && (val === false || val === 'false' ||
                                                val === '0' || val === 0 ||
                                                el8.util.isNone(val)) ) {
                elt.filter('[value="0"], [value="false"]').prop('checked', true);
            } else {
                elt.filter('[value="'+val+'"]').prop('checked', true);
            }
        } else if (elt.is("input:checkbox")) {
            elt.prop("checked", !!val);
        } else if (elt.is("select")) {
            if (val !== null) {
                elt.val(val);
            }
        } else {
            var maxLength = elt.prop('maxlength');
            // Current version of jQuery (1.5.2) returns -1 for maxlength if
            // maxlength is not present - we don't want to adjust val in that case.
            if (maxLength >= 0) {
                // JQuery .val() does not respect the maxlength attribute
                if (_.isString(val)) {
                    val = val.substr(0, maxLength);
                } else if (_.isNumber(val)) {
                    // notice "0.3" has length but only one significant digit,
                    // so this isn't a perfect filter
                    var maxFloatLen = Math.min(maxLength, 20); // avoid causing RangeError
                    val = parseFloat(val.toPrecision(maxFloatLen));
                }
            }
            elt.val(val);
        }
        if (!noTrigger) { elt.change(); }
    }
};

el8.uiwidget.Form.prototype.appendVal = function(name, val) {
    // Can only do to input fields
    if (!val) { return; }
    if (this._infoFields[name]) {
        var currVal = this._infoFields[name].getVal();
        if (currVal) {
            this._infoFields[name].setVal(currVal + '; ' + val);
        } else {
            this._infoFields[name].setVal(val);
        }
    }
};

el8.uiwidget.Form.prototype.getField = function(name) {
    return this._infoFields[name] || this.getInputField(name);
};

el8.uiwidget.Form.prototype.getInputField = function(name) {
    if (this._infoFields[name]) {
        //avoid running expensive attribute selector if possible
        return this._infoFields[name].getInputField();
    }
    return $('[name="'+name+'"]', this._formElt);
};

el8.uiwidget.Form.prototype.getMappedField = function(name) {
    var fieldSpec = this._getFieldSpec(name);
    return !fieldSpec.isDate ?
        this.getVal(name) :
        (!this.getVal(name) ? null :
            el8.service.tz.asServerFormatDate(el8.service.tz.newTzDate(this.getVal(name))));
};

el8.uiwidget.Form.prototype.getMapped = function(minimal, camelToUnderscore) {
    var mapped = {};
    for (var i=0; i<this._fields.length; i++) {
        var name = this._fields[i].name;
        var val = this.getMappedField(name);
        var mappedName = this._fields[i].mappedName || name;
        if (camelToUnderscore) {
          mappedName = mappedName.fromCamelToUnderscore();
        }
        if (minimal) {
            if (!el8.util.isNone(val)) {
                mapped[mappedName] = val;
            }
        } else {
            mapped[mappedName] = val;
        }
    }
    return mapped;
};

el8.uiwidget.Form.prototype.getMappedFields = function(fieldList) {
    var mapped = {};
    for (var i=0; i<fieldList.length; i++) {
        mapped[fieldList[i]] = this.getMappedField(fieldList[i]);
    }
    return mapped;
};

el8.uiwidget.Form.prototype.render = function() {
    if (!this._isInit) { this._init(); }
    this.reset();
    this.bindButtonEvents();
    this.enable();
    this.initFocus();
};

el8.uiwidget.Form.prototype.initFocus = function() {
    if (this._firstFocusSelector && !this._noFirstFocus) {
        $(this._firstFocusSelector, this._container).focusFocus();
    }
};

el8.uiwidget.Form.prototype.focusField = function(name) {
    if (this._infoFields[name]) {
        this._infoFields[name].getInputField().focusFocus();
    } else if (this._clickItems[name]) {
        if (this._clickItems[name].isEditMode()) {
            this._clickItems[name].focusField();
        }
    } else if (this._tagInputs[name]) {
        this._tagInputs[name].focusField();
    }

};

el8.uiwidget.Form.prototype.populate = function(spec, noTrigger) {
    for (var key in spec) {
        if (spec.hasOwnProperty(key)) {
            // Check if there's a field in the field spec that has a mappedName
            // equal to this key. If so, use the name of that field as the form
            // key to populate.
            var mappedIndex = this._fields.findIndex(function(field) {
              return field.mappedName === key;
            });
            var formKey = mappedIndex >= 0 ? this._fields[mappedIndex].name : key;
            this.setVal(formKey, spec[key], noTrigger);
        }
    }
};

el8.uiwidget.Form.prototype.getFormElt = function() {
    return this._formElt;
};

el8.uiwidget.Form.prototype.setFirstFieldSelector = function(selector) {
    var that = this;
    this._buttonSet.getTabCatcher().unbind('focus').on('focus', function(e) {
        $(selector, that._container)
            .scrollintoview()
            .focusSelect();
    });
};

el8.uiwidget.Form.prototype._getFieldSpec = function(name) {
    for (var i=0; i<this._fields.length; i++) {
        if (this._fields[i].name === name) { return this._fields[i]; }
    }
    return null;
};

el8.uiwidget.Form.prototype._createInfoField = function(spec) {
    return el8.service.infoField.addInfoField(spec);
};
